import * as QueryString from 'query-string';
import debugLib from 'debug';
import { GENERAL_COMMUNICATON_TOPICS } from '@branchjs/communicator';

import { mountFromLinkData } from '../../common/actions/ShareableActions';
import AnalyticsActions from '../actions/AnalyticsActions';
import { getAlerts } from '../actions/AlertNotificationsActions';

import dash_api from '../support/dash_api';
import { takeLeading } from '../support/utils';
import { getRangeInTimezoneEndingNow } from '../support/dateUtils';
import { APP_VIEW } from '../../common/support/viewConstants';
import { MicroFrontendContextPayloadProvider } from './MicroFrontendContextPayloadProvider';

import {
  WIDELY_USED_DISPATCHES,
  NON_API_DISPATCHES,
  ESSENTIAL_DISPATCHES,
} from 'client/js/main/routers/DispatchConfiguration/DispatchConfiguration';
import { InitialApiDispatcher } from 'client/js/main/routers/DispatchConfiguration/InitialApiDispatcher';

const debug = debugLib('MainApp');
const { ACCESS_TOKEN, CONTEXT, DASH_INIT, REFRESH_TOKEN } = GENERAL_COMMUNICATON_TOPICS;

const STAGE = 'stage.branch.io';
const isStage = () => window.location.origin.indexOf(STAGE) >= 0;

export const initMicroFrontendContext = (communicator) => {
  const microFrontendInitSubscription = communicator.on(DASH_INIT, () => {
    const payload = new MicroFrontendContextPayloadProvider({
      env: window.App?.context?.environment,
      view: window.App?.context?.view,
      origin: window.location?.origin,
      pendo: window?.App?.context?.pendoId,
      nonSSODashboardUrl: isStage() ? window.location.origin : window.API_BASE || window.location.origin,
      codeVersion: window?.App?.context?.codeVersion,
      sentry: {
        sentryEveryRequest: window?.App?.context?.flipper?.sentry_every_request,
        tracesSampleRate: window?.App?.context?.tracesSampleRate,
        errorSampleRate: window?.App?.context?.errorSampleRate,
        alertNotificationsId: window?.App?.context?.sentryAlertNotificationsId,
        journeysCreativesOptimizationId: window?.App?.context?.sentryJourneysCreativesOptimizationId,
        postbacksFormId: window?.App?.context?.sentryPostbacksFormId,
        qrCodeCustomizationsId: window?.App?.context?.sentryQrCodeCustomizationsId,
        quickLinkTemplatesId: window?.App?.context?.sentryQuickLinkTemplatesId,
        usageId: window?.App?.context?.sentryUsageId,
        discoDashboardId: window?.App?.context?.sentryDiscoDashboardId,
      },
      userId: window?.App?.context?.userId,
    }).getPayload();

    communicator.send(CONTEXT, payload);
  });
  return microFrontendInitSubscription;
};

export const initMicroFrontendAuthentication = (communicator) => {
  const microFrontendInitSubscription = communicator.on(DASH_INIT, async () => {
    try {
      const { data: accessToken } = await dash_api.get('/v1/authentication/access-token');
      communicator.send(ACCESS_TOKEN, { accessToken });
    } catch (error) {
      console.error(error);
      console.error('Unable to fetch access token');
    }
  });
  return microFrontendInitSubscription;
};

export const initMicroFrontendRefreshAuthentication = (communicator) => {
  const takeLeadingGetAccessToken = takeLeading(() => dash_api.get('/v1/authentication/access-token'));
  const microFrontendRefreshAuthSubscription = communicator.on(REFRESH_TOKEN, async () => {
    try {
      const res = await takeLeadingGetAccessToken();
      if (!res) {
        return;
      }
      const { data: accessToken } = res;
      communicator.send(ACCESS_TOKEN, { accessToken });
    } catch (error) {
      console.error(error);
      console.error('Unable to fetch access token');
    }
  });
  return microFrontendRefreshAuthSubscription;
};

export const ignite = async (
  store: any,
  settings: {
    view: { current_view: string };
    flipper: { custom_calculations: boolean; alert_notifications: boolean };
  }
) => {
  const dispatcher = new InitialApiDispatcher(
    [ESSENTIAL_DISPATCHES, WIDELY_USED_DISPATCHES, NON_API_DISPATCHES],
    store,
    settings
  );
  dispatcher.dispatch();

  if (settings.view.current_view === APP_VIEW) {
    if (settings.flipper.alert_notifications) {
      store.dispatch(getAlerts());
    }
  }
};

export const initAnalyticActions = () => {
  const query = QueryString.parse(location.search);
  if (query.date_range) {
    const date_range = getRangeInTimezoneEndingNow(parseInt(query.date_range as string));

    // @ts-ignore
    AnalyticsActions.setDate(...Object.values(date_range));
  }
};

export const initBranch = (tracking_disabled, store) => {
  window.branch.init('key_live_jpc4Hi6KhTtrwVHvdvMAQpokDxpB4Poc', { tracking_disabled }, (err, res) => {
    if (err) {
      debug('branchSDK.init: error', err);
      return;
    }
    if (res.data_parsed) {
      if (res.data_parsed.shareablePageState) {
        debug('branchSDK.init: shareableLinkData', res.data_parsed.shareablePageState);
        store.dispatch(mountFromLinkData(res.data_parsed.shareablePageState));
      }
    }
  });
};
